import { useState } from 'react';
import {
  addedWidget,
  freshWidgetId,
  hasRoomForWidget,
  movedWidgetTo,
  removedWidget,
  replacedWidget,
  shiftedWidget,
} from './layout-editing.ts';
import type { GridEditor } from './EditChrome.tsx';
import { widgetFromPreset, type WidgetPreset } from './widget-presets.ts';
import type { DashboardLayout, DashboardWidget } from './widget-types.ts';

export type DashboardEditor = {
  readonly draft: DashboardLayout | null;
  readonly dirty: boolean;
  readonly full: boolean;
  readonly configuring: DashboardWidget | null;
  readonly grid: GridEditor;
  readonly start: () => void;
  readonly cancel: () => void;
  readonly add: (preset: WidgetPreset) => void;
  readonly apply: (widget: DashboardWidget) => void;
  readonly closeSettings: () => void;
};

export const useDashboardEditor = (shown: DashboardLayout): DashboardEditor => {
  const [draft, setDraft] = useState<DashboardLayout | null>(null);
  const [configuringId, setConfiguringId] = useState<string | null>(null);
  const change = (edit: (layout: DashboardLayout) => DashboardLayout) => {
    setDraft((current) => edit(current ?? shown));
  };
  const layout = draft ?? shown;
  return {
    draft,
    dirty: draft !== null && draft !== shown,
    full: !hasRoomForWidget(layout),
    configuring: layout.widgets.find((widget) => widget.id === configuringId) ?? null,
    grid: {
      onShift: (id, shift) => {
        change((current) => shiftedWidget(current, id, shift));
      },
      onDropAt: (id, index) => {
        change((current) => movedWidgetTo(current, id, index));
      },
      onRemove: (id) => {
        change((current) => removedWidget(current, id));
      },
      onConfigure: setConfiguringId,
    },
    start: () => {
      setDraft(shown);
    },
    cancel: () => {
      setDraft(null);
      setConfiguringId(null);
    },
    add: (preset) => {
      change((current) =>
        addedWidget(current, widgetFromPreset(preset, freshWidgetId(current, preset.kind))),
      );
    },
    apply: (widget) => {
      change((current) => replacedWidget(current, widget));
      setConfiguringId(null);
    },
    closeSettings: () => {
      setConfiguringId(null);
    },
  };
};
